<template>
  <div class="profile-input-wrapper profile-input-address">
    <profile-form ref="form" method="post" endpoint="addresses" @error="onError($event)">
      <form class="form">
        <fieldset
          v-for="(localAddress, fieldsetIndex) in this.localProfile.addresses"
          :key="'fieldset' + fieldsetIndex"
          style="padding: 0"
        >
          <legend class="sr-only">{{ inputTitle }}</legend>
          <accordion-tab ref="accordionTab">
            <li slot-scope="{ toggleTab, expanded }" class="accordion-item">
              <button
                :id="'accordion-control' + fieldsetIndex"
                class="accordion-item-label"
                :class="expanded ? 'expanded' : ''"
                :aria-controls="'content' + fieldsetIndex"
                :aria-expanded="expanded ? 'true' : 'false'"
                v-on:click.prevent="toggleTab"
                v-on:enter.prevent="toggleTab"
                v-on:keydown.space.prevent="toggleTab"
              >
              <div class="accordion-item-label-text">
                <template v-if="allFieldsFilled(fieldsetIndex)">
                  {{ addressTypeLabel(localAddress.type) }} -
                  {{ localAddress.addressLineOne.slice(0, 19) }}
                  {{localAddress.addressLineOne.length > 19 ? '...' : ''}}
                </template>
                <template v-else> {{ tc("booking.newAddress") }} {{ tc('profile.formFields.address') }} </template>
              </div>
                <em class="icon-chevron-down accordion-item-label-icon"></em>
              </button>

              <div
                :id="'content' + fieldsetIndex"
                class="accordion-item-content"
                :aria-hidden="expanded ? 'false' : 'true'"
              >
                <div class="accordion-item-content-wrapper">
                  <div class="input radio">
                    <label
                      class="profile-input-radio"
                      v-for="addressType in addressesTypes"
                      :key="addressType.value + fieldsetIndex"
                      :class="[{'disabled': isSelected(addressType.value)}]"
                    >
                      <input
                        v-model="localAddress.type"
                        @change="resetValidations()"
                        type="radio"
                        :name="'type' + fieldsetIndex"
                        class="req"
                        :class="[
                          { 'no-value': !localAddress.type },
                          { valid: !!localAddress.type }
                        ]"
                        :id="addressType.value + fieldsetIndex"
                        :value="Number(addressType.value)"
                        data-val="true"
                        :data-val-required="getValidationForField('required')"
                      />
                      {{ addressType.label }}
                    </label>
                    <span
                      :data-valmsg-for="'type' + fieldsetIndex"
                      data-valmsg-replace="true"
                      class="field-validation-valid"
                    ></span>
                  </div>
                  <div class="input dropdown">
                    <select
                      :class="[
                        { 'no-value': !localAddress.country },
                        { valid: !!localAddress.country }
                      ]"
                      @change="countryChange($event, fieldsetIndex)"
                      :id="'country' + fieldsetIndex"
                      :name="'country' + fieldsetIndex"
                      v-model="localAddress.country"
                      class="req"
                      data-field-type="DropdownList"
                      data-val="true"
                      :data-val-required="getValidationForField('required', 'country')"
                    >
                      <option
                        v-for="(country, i) in countries"
                        :key="country.value + i"
                        :value="country.value"
                        @click="this.states = country.states"
                      >
                        {{ country.label }}
                      </option>
                    </select>

                    <label :for="'country' + fieldsetIndex" class="req floating">
                      {{ tc('profile.formFields.country') }}
                    </label>
                    <span
                      :data-valmsg-for="'country' + fieldsetIndex"
                      data-valmsg-replace="true"
                      class="field-validation-valid"
                    ></span>
                  </div>
                  <div v-if="Number(localAddress.type) === 2" class="input">
                    <input
                      :class="[
                        { 'no-value': !localAddress.companyName },
                        { valid: !!localAddress.companyName },
                        { 'input-validation-error': error }
                      ]"
                      class="req"
                      type="text"
                      maxlength="255"
                      :id="'companyName' + fieldsetIndex"
                      :name="'companyName' + fieldsetIndex"
                      v-model="localAddress.companyName"
                      data-field-type="SingleLineText"
                      data-val="true"
                      :data-val-required="getValidationForField('required', 'companyName')"
                      data-rule-minlength="1"
                      data-rule-maxlength="255"
                      :data-msg-minlength="getValidationForField('minChars', '1')"
                      :data-msg-maxlength="getValidationForField('maxChars', '255')"
                    />

                    <label :for="'companyName' + fieldsetIndex" class="req floating">
                      {{ tc('profile.formFields.companyName') }}
                    </label>
                    <span
                      :data-valmsg-for="'companyName' + fieldsetIndex"
                      data-valmsg-replace="true"
                      class="field-validation-valid"
                    ></span>
                  </div>
                  <div class="input">
                    <input
                      :class="[
                        { 'no-value': !localAddress.addressLineOne },
                        { valid: !!localAddress.addressLineOne }
                      ]"
                      class="req"
                      type="text"
                      maxlength="255"
                      :id="'addressLineOne' + fieldsetIndex"
                      :name="'addressLineOne' + fieldsetIndex"
                      v-model="localAddress.addressLineOne"
                      data-field-type="SingleLineText"
                      data-val="true"
                      :data-val-address="tc('validations.address')"
                      :data-val-required="getValidationForField('required', 'addressLineOne')"
                      data-rule-minlength="1"
                      data-rule-maxlength="255"
                      :data-msg-minlength="getValidationForField('minChars', '1')"
                      :data-msg-maxlength="getValidationForField('maxChars', '255')"
                    />

                    <label :for="'addressLineOne' + fieldsetIndex" class="req floating">
                      {{ tc('profile.formFields.addressLineOne') }}
                    </label>
                    <span
                      :data-valmsg-for="'addressLineOne' + fieldsetIndex"
                      data-valmsg-replace="true"
                      class="field-validation-valid"
                    ></span>
                  </div>
                  <div class="input">
                    <input
                      :class="[
                        { 'no-value': !localAddress.addressLineTwo },
                        { valid: !!localAddress.addressLineTwo }
                      ]"
                      type="text"
                      maxlength="255"
                      :id="'addressLineTwo' + fieldsetIndex"
                      :name="'addressLineTwo' + fieldsetIndex"
                      v-model="localAddress.addressLineTwo"
                      :data-val-address="tc('validations.address')"
                      data-rule-minlength="1"
                      data-rule-maxlength="255"
                      :data-msg-minlength="getValidationForField('minChars', '1')"
                      :data-msg-maxlength="getValidationForField('maxChars', '255')"
                      data-field-type="SingleLineText"
                    />

                    <label :for="'addressLineTwo' + fieldsetIndex" class="floating">
                      {{ tc('profile.formFields.addressLineTwo') }}
                    </label>
                  </div>
                  <div class="input">
                    <input
                      :class="[{ 'no-value': !localAddress.city }, { valid: !!localAddress.city }]"
                      class="req"
                      v-model="localAddress.city"
                      type="text"
                      :id="'city' + fieldsetIndex"
                      :name="'city' + fieldsetIndex"
                      maxlength="255"
                      data-field-type="SingleLineText"
                      data-val="true"
                      :data-val-required="getValidationForField('required', 'city')"
                      data-rule-minlength="1"
                      data-rule-maxlength="50"
                      :data-msg-minlength="getValidationForField('minChars', '1')"
                      :data-msg-maxlength="getValidationForField('maxChars', '50')"
                    />

                    <label :for="'city' + fieldsetIndex" class="req floating">
                      {{ tc('profile.formFields.city') }}
                    </label>
                    <span
                      :data-valmsg-for="'city' + fieldsetIndex"
                      data-valmsg-replace="true"
                      class="field-validation-valid"
                    ></span>
                  </div>
                  <div class="flex-grid-2-col gutter-lg">
                    <div class="input" :class="[{ dropdown: haveStates(fieldsetIndex) }]">
                      <input
                        v-if="!haveStates(fieldsetIndex)"
                        :class="[
                          { 'no-value': !localAddress.state },
                          { valid: !!localAddress.state }
                        ]"
                        type="text"
                        :id="'state' + fieldsetIndex"
                        v-model="localAddress.state"
                        :name="'state' + fieldsetIndex"
                        maxlength="255"
                        data-rule-minlength="1"
                        data-rule-maxlength="255"
                        :data-msg-minlength="getValidationForField('minChars', '1')"
                        :data-msg-maxlength="getValidationForField('maxChars', '255')"
                        data-field-type="SingleLineText"
                      />
                      <select
                        v-else
                        :class="[
                          { 'no-value': !localAddress.state },
                          { 'has-value': localAddress.state },
                          { valid: !!localAddress.state }
                        ]"
                        :id="'state' + fieldsetIndex"
                        :name="'state' + fieldsetIndex"
                        v-model="localAddress.state"
                        class="req"
                        data-field-type="DropdownList"
                        data-val="true"
                        :data-val-required="getValidationForField('required', 'state')"
                      >
                        <option
                          v-for="state in statesList(fieldsetIndex)"
                          :key="state.value"
                          :value="state.value"
                        >
                          {{ state.label }}
                        </option>
                      </select>

                      <label
                        :for="'state' + fieldsetIndex"
                        class="floating"
                        :class="[{ req: haveStates(fieldsetIndex) }]"
                      >
                        {{ tc('profile.formFields.state') }}
                      </label>

                      <span
                        :data-valmsg-for="'state' + fieldsetIndex"
                        data-valmsg-replace="true"
                        class="field-validation-valid"
                      ></span>
                    </div>
                    <div class="input">
                      <input
                        :class="[
                          { 'no-value': !localAddress.postalCode },
                          { valid: !!localAddress.postalCode }
                        ]"
                        class="req"
                        type="text"
                        :id="'postalCode' + fieldsetIndex"
                        :name="'postalCode' + fieldsetIndex"
                        maxlength="255"
                        v-model="localAddress.postalCode"
                        data-field-type="SingleLineText"
                        data-val="true"
                        :data-val-zipcode="tc('validations.zipcode')"
                        :data-val-required="getValidationForField('required', 'zipCode')"
                        data-rule-minlength="1"
                        data-rule-maxlength="26"
                        :data-msg-minlength="getValidationForField('minChars', '1')"
                        :data-msg-maxlength="getValidationForField('maxChars', '26')"
                      />

                      <label :for="'postalCode' + fieldsetIndex" class="req floating">
                        {{ tc('profile.formFields.zipCode') }}
                      </label>
                      <span
                        :data-valmsg-for="'postalCode' + fieldsetIndex"
                        data-valmsg-replace="true"
                        class="field-validation-valid"
                      ></span>
                    </div>
                  </div>
                  <div class="address-remove">
                    <button
                      v-if="showRemoveBtn(fieldsetIndex)"
                      class="address-remove-btn"
                      @click.prevent="removeAddress(fieldsetIndex)"
                    >
                      <span class="code-text">{{ tc("booking.remove") }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </li>
          </accordion-tab>
          <button
            v-if="addressesLength < 2 && allFieldsFilled(fieldsetIndex)"
            class="address-add"
            @click.prevent="initNewAddress"
          >
            <div class="icon-add-gold"></div>
            <span class="code-text">{{ tc('booking.add') }} {{ tc('profile.formFields.address') }}</span>
          </button>
        </fieldset>
        <fieldset :style="{visibility: showPreferredSelect ? 'inherit' : 'hidden'}">
          <span class="profile-input-label">{{ tc('profile.formFields.selectPreferredAddress') }}:</span>
          <div class="input radio address-preferred">
            <label
              class="profile-input-radio"
              v-for="(address, index) in localProfile.addresses"
              :key="'preferred-' + index"
            >
              <input
                v-model="preferredAddress"
                type="radio"
                name="preferredAddress"
                :id="'preferredAddress-' + index"
                :value="index"
              />
              <template v-if="allFieldsFilled(index)">
              {{ addressTypeLabel(address.type) }} -
              {{ address.addressLineOne }}
              </template>
              <template v-else>
                {{ tc("booking.newAddress") }} {{ tc('profile.formFields.address') }}
              </template>
            </label>
          </div>
        </fieldset>
        <input type="hidden" :value="true" name="isAddresses" />
      </form>
    </profile-form>
  </div>
</template>

<script>
import ProfileInputMixin from '@mixins/profile-input-mixin'

export default {
  name: 'profile-address-input',
  mixins: [ProfileInputMixin],
  props: {
    addressesTypes: {
      type: Array,
      required: true
    },
    countries: {
      type: Array,
      required: true
    }
  },
  data: () => ({
    localProfile: {
      addresses: []
    },
    error: null,
    preferredAddress: 0
  }),
  mounted() {
    this.configureModalStyle()
    if(this.addressesLength === 0){
      this.initNewAddress()
    }
    this.$nextTick(() => {
      this.openClosedAccordions()
    })
  },
  computed: {
    showPreferredSelect(){
      return this.addressesLength > 1
    },
    addressesList(){
      return this.localProfile.addresses ? this.localProfile.addresses : []
    },
    addressesLength() {
      return this.addressesList ? this.addressesList.length : 0
    }
  },
  methods: {
    resetValidations() {
      this.$nextTick(() => {
        if(this.$refs.form) {
          this.$refs.form.reInitForm()
        }
      })
    },
    allFieldsFilled(index){
      const isCompany = this.addressesList[index]?.type === 2
      const neededFields = ['type', 'country', 'addressLineOne', 'city', 'postalCode']
      isCompany ? neededFields.push('companyName') : null
      this.haveStates(index) ? neededFields.push('state') : null

      const haveNeededFields = this.addressesList[index] && neededFields
            .map((f) => (Array.isArray(this.addressesList[index][f]) ? this.addressesList[index][f].length > 0 : !!this.addressesList[index][f]))
            .filter((f) => {
              return !!f
              }).length === neededFields.length

      return haveNeededFields
    },
    openClosedAccordions(){
      this.resetValidations()
      if(this.$refs.accordionTab) {
        this.$refs.accordionTab.forEach((el)=>{
          if(!el.expanded){
            el.toggleTab()
          }
        })
      }
    },
    openLastAccordion(){
      this.resetValidations()
      if(this.$refs.accordionTab) {
        if(!this.$refs.accordionTab[1].expanded){
          this.$refs.accordionTab[1].toggleTab()
        }
      }
    },
    onError(e){
      this.openClosedAccordions()
      this.$emit('done', e)
    },
    configureModalStyle() {
      const modalBody = document.querySelector('.modal-body')
      modalBody.style.padding = '0 40px 0 40px'
    },
    userAddress(fieldsetIndex) {
      return this.localProfile.addresses[fieldsetIndex] || {}
    },
    statesList(fieldsetIndex) {
      const selected = this.countries.find(
        (el) => el.value === this.localProfile.addresses[fieldsetIndex].country
      )
      if (selected) {
        return selected.states
      }
      return []
    },
    availableStates(fieldsetIndex) {
      return this.localProfile.addresses[fieldsetIndex].country
        ? this.countries.find((c) => c.value === this.localProfile.addresses[fieldsetIndex].country)
            ?.states
        : null
    },
    haveStates(fieldsetIndex) {
      return this.availableStates(fieldsetIndex) && this.availableStates(fieldsetIndex).length > 0
    },
    countryChange(e, index) {
      this.localProfile.addresses[index].state = null
      this.resetValidations()
    },
    pushEmptyAddress(){
      this.localProfile.addresses.push({
        type: null,
        companyName: '',
        country: '',
        state: '',
        city: '',
        postalCode: '',
        addressLineOne: '',
        addressLineTwo: '',
        addressTypeLabel: '',
        preferred: false,
        isNewAddress: true
      })
    },
    initNewAddress() {
      this.pushEmptyAddress()
      if (this.$refs.form) {
        this.$nextTick(() => {
          this.$refs.form.reInitForm()
          this.openLastAccordion()
        })
      }
    },
    async removeAddress(index) {
      this.isLoading = true
      this.localProfile.addresses.splice(index, 1)
      if(!this.userAddress(index).isNewAddress){
        this.$refs.form.form.submit()
      }
      this.isLoading = false
    },
    addressTypeLabel(type){
      return this.addressesTypes.find(el => Number(el.value) === Number(type))?.label
    },
    isSelected(type) {
      return this.localProfile.addresses.some((address) => {
        if (Number(address.type) === Number(type)) {
          return true
        }
      })
    },
     showRemoveBtn(index) {
      const hasMoreThanOneAddress = this.addressesLength > 1
      const isNewAddress = !!this.addressesList[index]?.isNewAddress
      const allAddressAreSaved = this.addressesList.map((address) => { return !!address?.isNewAddress === false })
      .filter((f) => { return !!f}).length === this.addressesLength

      return hasMoreThanOneAddress && (isNewAddress || allAddressAreSaved)
    }
  }
}
</script>
